/**
 * DualGoalProgressBar — two-row stacked bar for Sprint and Balance of year.
 *
 * Row 1: Sprint progress = (closed_won + committed) / sprint_goal.
 * Row 2: BOY progress    = (closed_won + committed) / boy_goal.
 */
import { useMyGoals } from "@/lib/useMyGoals";

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function clampPct(num: number, denom: number): number {
  if (!denom || denom <= 0) return 0;
  return Math.max(0, Math.min(100, (num / denom) * 100));
}

export function DualGoalProgressBar() {
  const { goals, loading } = useMyGoals();

  const sprintProgress = goals.sprintActual + goals.sprintCommitted;
  const boyProgress = goals.boyActual + goals.boyCommitted;
  const sprintPct = clampPct(sprintProgress, goals.sprintGoal);
  const boyPct = clampPct(boyProgress, goals.boyGoal);

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="space-y-3">
        {/* Sprint row */}
        <div>
          <div className="flex items-baseline justify-between text-xs text-neutral-600">
            <span className="font-medium text-neutral-900">Accelerated Jumpstart</span>
            <span>
              {loading ? "..." : `${fmt(sprintProgress)} / ${fmt(goals.sprintGoal)}`}
            </span>
          </div>
          <div className="mt-1 h-2 w-full rounded-full bg-neutral-100 overflow-hidden">
            <div
              className="h-full bg-[#DC2626] transition-all"
              style={{ width: `${sprintPct}%` }}
              data-testid="dual-goal-sprint-bar"
            />
          </div>
        </div>

        {/* BOY row */}
        <div>
          <div className="flex items-baseline justify-between text-xs text-neutral-600">
            <span className="font-medium text-neutral-900">Balance of year</span>
            <span>
              {loading ? "..." : `${fmt(boyProgress)} / ${fmt(goals.boyGoal)}`}
            </span>
          </div>
          <div className="mt-1 h-2 w-full rounded-full bg-neutral-100 overflow-hidden">
            <div
              className="h-full bg-[#0B0B0F] transition-all"
              style={{ width: `${boyPct}%` }}
              data-testid="dual-goal-boy-bar"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DualGoalProgressBar;
